import SingleEmployeeBreadcrumbs from '../../shared/SingleEmployeeBreadcrumbs';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEmployeeService, useEmployeeUrl } from 'domain/shared/employee';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { DocumentTemplatingWithPreview, DocumentTemplateForm } from 'domain/shared/documentStorage';
import { DEFAULT_ENTITY_AUTHOR } from 'domain/shared/core/constants';
import { FormShowTemplate, DEFAULT_ERROR_MESSAGE } from 'framework/form';
import { EntityHeader, EntityProfileLayout, ShowPageLayout } from 'framework/ui/layout';
import EmployeeDocumentBreadcrumbItem from '../shared/EmployeeDocumentBreadcrumbItem';
import { useDocumentStorageContext } from 'framework/documentStorage';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { DocumentStorageConfig } from 'framework/documentStorage';
import {
  buildFormValues,
  buildRenderPayload,
  disablePolishOfficePreviewLockedFields,
  mapTemplateFieldsForForm,
} from '../helpers/templatePreviewPayloadHelper';
import {
  EMPLOYEE_DOCUMENT_OTHER_TYPES,
  EMPLOYEE_DOCUMENT_SECTIONS,
  EMPLOYEE_DOCUMENT_STAGES_ALIAS,
} from '../constants';
import { buildPreviewPayload } from 'domain/shared/documentStorage/shared/DocumentTemplatePreviewSection';
import { isPolishOffice } from 'domain/shared/employee';

const GenerateEmployeeDocument = ({ isEmpoyeePortal: isMyProfile }: { isEmpoyeePortal?: boolean }) => {
  const currentAlias = useEmployeeId()!;
  const { documentPath } = useParams();
  const [searchParams] = useSearchParams();
  const { addMessageAndRedirect } = useMessageService();
  const { getById } = useEmployeeService();
  const { getEmployeeDocumentsUrl, getSingleEntityUrl } = useEmployeeUrl();
  const { uploadDocument, getDocumentTemplateConfig, renderPreviewDocumentTemplate } = useDocumentStorageContext();

  const decodedDocumentPath = decodeURIComponent(documentPath!);
  const allParams = Object.fromEntries(searchParams.entries());

  const getShowData = async () => {
    try {
      const employee = await getById(currentAlias);
      const config = (await getDocumentTemplateConfig!(decodedDocumentPath, allParams)) as DocumentStorageConfig;
      const isPolishEmployee = !!employee?.officeId && isPolishOffice(String(employee.officeId));

      const formFields = isPolishEmployee ? disablePolishOfficePreviewLockedFields(config.fields) : config.fields;
      const formValues = buildFormValues(config.fields);
      const previewPayload = buildPreviewPayload(buildRenderPayload(config.fields));

      const renderedTemplate = renderPreviewDocumentTemplate
        ? await renderPreviewDocumentTemplate({
            templatePath: decodedDocumentPath,
            ...previewPayload,
          })
        : null;

      return {
        nameEn: employee.nameEn,
        documentPath: renderedTemplate?.path || decodedDocumentPath,
        sourceTemplatePath: decodedDocumentPath,
        formTitlePath: decodedDocumentPath,
        ...config,
        fields: mapTemplateFieldsForForm(formFields),
        form: formValues,
      };
    } catch {
      return addMessageAndRedirect(
        new ErrorMessage(DEFAULT_ERROR_MESSAGE),
        isMyProfile ? getSingleEntityUrl('me') : getEmployeeDocumentsUrl(),
      );
    }
  };

  return (
    <FormShowTemplate
      getDataFunc={getShowData}
      submitFormFunc={async data => {
        return uploadDocument({
          employeeAlias: currentAlias,
          paths: [data.documentTempPath],
          section: data.metadata.section || EMPLOYEE_DOCUMENT_SECTIONS.OTHER,
          fileType: data.metadata.fileType || EMPLOYEE_DOCUMENT_OTHER_TYPES.OTHER,
          stage: data.metadata.stage || EMPLOYEE_DOCUMENT_STAGES_ALIAS.UPLOADED,
          office: data.metadata.office,
          number: data.metadata.number,
          category: data.metadata.category,
        });
      }}
      afterSubmit={{
        redirect: isMyProfile ? getSingleEntityUrl('me') : getEmployeeDocumentsUrl(),
        message: 'You are awesome! The Document has been successfully uploaded.',
      }}
      initEditMode
    >
      <ShowPageLayout
        breadcrumbs={
          !isMyProfile ? (
            <SingleEmployeeBreadcrumbs>
              <EmployeeDocumentBreadcrumbItem filePath={decodedDocumentPath} />
            </SingleEmployeeBreadcrumbs>
          ) : null
        }
        entityHeader={<EntityHeader hasAuthor={false} defaultAuthor={DEFAULT_ENTITY_AUTHOR} />}
        hasSeparateSections
      >
        <EntityProfileLayout
          leftColumnElement={<DocumentTemplateForm />}
          rightColumnElement={<DocumentTemplatingWithPreview />}
        />
      </ShowPageLayout>
    </FormShowTemplate>
  );
};

export default GenerateEmployeeDocument;
